// src/pages/AdminDashboard.js
import React, { useContext, useEffect, useState } from "react";
import API from "../api";
import { UserContext } from "../context/UserContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment-timezone";
import ConfirmModal from "../components/ConfirmModal";
import AlertModal from "../components/AlertModal";
import { usePageTitle } from "../hooks/usePageTitle";
import {
  FaPlusCircle,
  FaTimes,
  FaFootballBall,
} from "react-icons/fa";
import LoadingSpinner from "../components/LoadingSpinner";
import Card from "../components/ui/Card";
import AdminAuditPanel from "../components/admin/AdminAuditPanel";
import {
  WC_DEFAULT_STAGE,
  WC_STAGE_OPTIONS,
} from "../constants/worldCup";

const COMPETITIONS_WITHOUT_LEAGUE_TABLE = ["world_cup", "ucl", "wildcard"];

// ⚙️ Admin league table component (only for Premier League)
const AdminLeagueTable = ({ competition }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Modal states
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: "", message: "", onConfirm: null });
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: "", message: "", type: "info" });

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/teams", {
        params: { competition },
      });
      setTeams(data);
      setError("");
    } catch (err) {
      console.error("❌ Error fetching teams:", err.response || err);
      setError("⚠️ No teams found.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, [competition]);

  const handleSeedTeams = async () => {
    setConfirmModal({
      isOpen: true,
      title: "Reset League Table",
      message: "⚠️ This will reset the league table. Continue?",
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: "", message: "", onConfirm: null });
        executeSeedTeams();
      }
    });
  };

  const executeSeedTeams = async () => {
    try {
      const { data } = await API.post("/admin/seed-teams", {
        competition,
      });
      setAlertModal({ isOpen: true, title: "Success", message: "✅ " + data.message, type: "success" });
      fetchTeams();
    } catch (err) {
      console.error("❌ Error seeding teams:", err.response || err);
      setAlertModal({ isOpen: true, title: "Error", message: "❌ Failed to seed teams.", type: "error" });
    }
  };

  const handleSaveTable = async () => {
    const tableData = teams.map((team, idx) => ({
      position: idx + 1,
      team_name: team.team_name,
      points: Number(team.points),
      competition,
    }));
    try {
      await API.post("/admin/update-league-table", { tableData });
      setAlertModal({ isOpen: true, title: "Success", message: "✅ League table updated successfully!", type: "success" });
      fetchTeams();
    } catch (err) {
      console.error("❌ Error saving table:", err.response || err);
      setAlertModal({ isOpen: true, title: "Error", message: "❌ Failed to save table.", type: "error" });
    }
  };

  return (
    <>
      <Card title="🏆 Premier League Table">
        <div className="space-y-4">
          <button
            onClick={handleSeedTeams}
            className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover transition-all duration-200 font-semibold"
          >
            ⚽ Seed Teams
          </button>
          {loading ? (
            <LoadingSpinner message="Loading admin data..." />
          ) : error ? (
            <p className="text-gray-500">{error}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-3 text-left font-semibold text-gray-700">Pos</th>
                    <th className="p-3 text-left font-semibold text-gray-700">Team</th>
                    <th className="p-3 text-left font-semibold text-gray-700">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {teams.map((team, idx) => (
                    <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="p-3 text-center font-medium">{idx + 1}</td>
                      <td className="p-3 font-medium">{team.team_name}</td>
                      <td className="p-3">
                        <input
                          type="number"
                          value={team.points}
                          onChange={(e) => {
                            const updated = [...teams];
                            updated[idx].points = e.target.value;
                            setTeams(updated);
                          }}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <button
            onClick={handleSaveTable}
            className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover transition-all duration-200 font-semibold"
          >
            💾 Save Table
          </button>
        </div>
      </Card>
      
      {/* Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: "", message: "", onConfirm: null })}
      />
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ isOpen: false, title: "", message: "", type: "info" })}
      />
    </>
  );
};

const AdminDashboard = () => {
  const { user } = useContext(UserContext);
  usePageTitle('Admin Dashboard');
  
  const [competition, setCompetition] = useState("premier_league");
  const [activeTab, setActiveTab] = useState("controls");
  const [gameweeks, setGameweeks] = useState([]);
  const [selectedGameweek, setSelectedGameweek] = useState("");
  const [loadingGameweeks, setLoadingGameweeks] = useState(false);

  const [fixtures, setFixtures] = useState([]);
  const [loadingFixtures, setLoadingFixtures] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  const [newGameweek, setNewGameweek] = useState({
    week_number: "",
    start_date: null,
    end_date: null,
    prediction_open_time: null,
    prediction_deadline: null,
  });

  const [newMatch, setNewMatch] = useState({
    home_team: "",
    away_team: "",
    match_date: null,
  });

  // Bulk matches for easier admin entry
  const [bulkMatches, setBulkMatches] = useState([
    { home_team: "", away_team: "", match_date: null },
  ]);

  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [postponePickerFixture, setPostponePickerFixture] = useState(null);
  const [postponePickerDate, setPostponePickerDate] = useState(null);

  // Global modals
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: "", message: "", type: "info" });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: "", message: "", onConfirm: null });

  // Fixtures filters
  const [filterPreset, setFilterPreset] = useState("next7"); // last7 | next7 | custom
  const [filterStart, setFilterStart] = useState(null);
  const [filterEnd, setFilterEnd] = useState(null);
  const [syncProgress, setSyncProgress] = useState({
    running: false,
    steps: { standings: "idle", results: "idle", fixtures: "idle" },
    summary: null,
    error: "",
  });
  const [syncHistory, setSyncHistory] = useState([]);
  const [predictionLockMinutes, setPredictionLockMinutes] = useState(30);
  const [savingPredictionSettings, setSavingPredictionSettings] = useState(false);
  const [recomputingDeadlines, setRecomputingDeadlines] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingAdminUsers, setLoadingAdminUsers] = useState(false);
  const [savingUserId, setSavingUserId] = useState(null);
  const [wcSyncStage, setWcSyncStage] = useState(WC_DEFAULT_STAGE);
  const [tableLockStatus, setTableLockStatus] = useState({
    locked: false,
    manualLock: false,
    pastDeadline: false,
    deadline: null,
  });
  const [tableDeadline, setTableDeadline] = useState(null);
  const [savingTableDeadline, setSavingTableDeadline] = useState(false);
  const [testEmailTemplate, setTestEmailTemplate] = useState("24h");
  const [testEmailTo, setTestEmailTo] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  useEffect(() => {
    if (user?.email) {
      setTestEmailTo((prev) => prev || user.email);
    }
  }, [user?.email]);

  const buildWorldCupSyncBody = (overrides = {}) => {
    const body = {
      competition: "world_cup",
      fullSeason: false,
      ...overrides,
    };
    if (wcSyncStage) body.stage = wcSyncStage;
    return body;
  };

  // Fetch fixtures helper (uses state above)
  const fetchAdminFixtures = async () => {
    setLoadingFixtures(true);
    try {
      const { data } = await API.get("/gameweeks/all-fixtures", {
        params: { competition, includePostponed: true },
      });

      setFixtures(
        Array.isArray(data)
          ? data.map((f) => ({ ...f, manual_override: !!f.manual_override }))
          : []
      );
    } catch (err) {
      console.error("❌ Error fetching fixtures:", err.response || err);
      setAlertModal({
        isOpen: true,
        title: "Error",
        message: "❌ Error fetching fixtures.",
        type: "error",
      });
    } finally {
      setLoadingFixtures(false);
    }
  };

  const filteredFixtures = fixtures.filter((f) => {
    if (!f.match_date) return true;
    const md = new Date(f.match_date);

    let start = filterStart;
    let end = filterEnd;

    const now = new Date();
    if (filterPreset === "last7") {
      start = new Date(now.getTime() - 7 * 86400000);
      end = now;
    } else if (filterPreset === "next7") {
      start = now;
      end = new Date(now.getTime() + 7 * 86400000);
    }

    if (start && md < start) return false;
    if (end && md > end) return false;
    return true;
  });

  // Gameweeks/stages are not required anymore; clear any stale UI state on competition switch
  useEffect(() => {
    setGameweeks([]);
    setSelectedGameweek("");
    setFixtures([]);
  }, [competition, timezone]);

  // Fetch fixtures by competition only (GW/stage free)
  useEffect(() => {
    fetchAdminFixtures();
  }, [competition]);

  // Fetch team list per competition for dropdowns
  useEffect(() => {
    if (COMPETITIONS_WITHOUT_LEAGUE_TABLE.includes(competition)) {
      const names = new Set();
      fixtures.forEach((f) => {
        if (f.home_team) names.add(f.home_team);
        if (f.away_team) names.add(f.away_team);
      });
      setTeamOptions([...names].sort().map((team_name) => ({ team_name })));
      setLoadingTeams(false);
      return;
    }

    const fetchTeams = async () => {
      setLoadingTeams(true);
      try {
        const { data } = await API.get("/admin/teams", { params: { competition } });
        setTeamOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("❌ Error fetching team list:", err.response || err);
        setTeamOptions([]);
      } finally {
        setLoadingTeams(false);
      }
    };
    fetchTeams();
  }, [competition, fixtures]);

  useEffect(() => {
    const fetchPredictionSettings = async () => {
      try {
        const { data } = await API.get("/admin/prediction-settings", {
          params: { competition },
        });
        setPredictionLockMinutes(
          Number.isFinite(Number(data?.prediction_lock_minutes))
            ? Number(data.prediction_lock_minutes)
            : 30
        );
      } catch (err) {
        console.error("❌ Error fetching prediction settings:", err.response || err);
        setPredictionLockMinutes(30);
      }
    };
    fetchPredictionSettings();
  }, [competition]);

  useEffect(() => {
    const fetchTableSettings = async () => {
      if (!["premier_league", "laliga", "bundesliga", "serie_a"].includes(competition)) {
        setTableLockStatus({
          locked: false,
          manualLock: false,
          pastDeadline: false,
          deadline: null,
        });
        setTableDeadline(null);
        return;
      }
      try {
        const { data } = await API.get("/admin/table-prediction-settings", {
          params: { competition },
        });
        setTableLockStatus({
          locked: !!data?.locked,
          manualLock: !!data?.manualLock,
          pastDeadline: !!data?.pastDeadline,
          deadline: data?.deadline || null,
        });
        setTableDeadline(data?.deadline ? new Date(data.deadline) : null);
      } catch (err) {
        console.error("❌ Error fetching table prediction settings:", err);
      }
    };
    fetchTableSettings();
  }, [competition]);

  const handleCompetitionChange = (nextCompetition) => {
    const value =
      typeof nextCompetition === "string"
        ? nextCompetition
        : nextCompetition?.target?.value;
    if (!value) return;
    setCompetition(value);
    setNewMatch({ home_team: "", away_team: "", match_date: null });
    setNewGameweek({
      week_number: "",
      start_date: null,
      end_date: null,
      prediction_open_time: null,
      prediction_deadline: null,
    });
    setSelectedGameweek("");
  };

  const competitions = [
    { key: "premier_league", label: "Premier League" },
    { key: "laliga", label: "LaLiga" },
    { key: "bundesliga", label: "Bundesliga" },
    { key: "serie_a", label: "Serie A" },
    { key: "world_cup", label: "World Cup" },
    { key: "wildcard", label: "Wildcard" },
    { key: "ucl", label: "Champions League" },
  ];

  const tabMeta = {
    controls: { title: "Controls", hint: "Run sync jobs and lock settings" },
    matches: { title: "Matches & Competitions", hint: "Manage fixtures and league table" },
    users: { title: "Users", hint: "Accounts, verification, admin and table-prediction access" },
    audit: { title: "Audit", hint: "Logins, predictions, scoring breakdown, sync history" },
  };

  const getSyncStatusClass = (status) => {
    if (status === "done") return "bg-green-100 text-green-700 border-green-200";
    if (status === "running") return "bg-blue-100 text-blue-700 border-blue-200";
    if (status === "failed") return "bg-red-100 text-red-700 border-red-200";
    if (status === "queued") return "bg-amber-100 text-amber-700 border-amber-200";
    return "bg-gray-100 text-gray-600 border-gray-200";
  };

  const addSyncHistory = (action, status, message) => {
    setSyncHistory((prev) => [
      { action, status, message, at: new Date().toISOString() },
      ...prev.slice(0, 5),
    ]);
  };

  const handleSyncWorldCupFull = async () => {
    setAlertModal({
      isOpen: true,
      title: "Syncing",
      message: "Syncing full World Cup 2026 schedule (may take 1–2 minutes)...",
      type: "info",
    });
    try {
      const body = wcSyncStage ? { stage: wcSyncStage } : {};
      const { data } = await API.post("/admin/world-cup/sync-full", body, {
        timeout: 180000,
      });
      const message =
        data?.message ||
        `Synced ${data?.total ?? 0} matches (${data?.inserted ?? 0} new, ${data?.updated ?? 0} updated).`;
      setAlertModal({ isOpen: true, title: "Success", message: `✅ ${message}`, type: "success" });
      addSyncHistory("World Cup full sync", "success", message);
      fetchAdminFixtures();
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Failed to sync World Cup.";
      console.error("❌ Error syncing World Cup:", err.response || err);
      setAlertModal({ isOpen: true, title: "Error", message: `❌ ${message}`, type: "error" });
      addSyncHistory("World Cup full sync", "failed", message);
    }
  };

  const handleSyncStandings = async () => {
    setAlertModal({ isOpen: true, title: "Syncing", message: "Syncing standings...", type: "info" });
    try {
      const { data } = await API.post("/admin/sync-standings", { competition });
      const message = data?.message || "Standings synced.";
      setAlertModal({ isOpen: true, title: "Success", message: `✅ ${message}`, type: "success" });
      addSyncHistory("Standings", "success", message);
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Failed to sync standings.";
      console.error("❌ Error syncing standings:", err.response || err);
      setAlertModal({ isOpen: true, title: "Error", message: `❌ ${message}`, type: "error" });
      addSyncHistory("Standings", "failed", message);
    }
  };

  const handleSyncResults = async () => {
    setAlertModal({ isOpen: true, title: "Syncing", message: "Syncing results...", type: "info" });
    try {
      const body =
        competition === "world_cup"
          ? buildWorldCupSyncBody({ daysBack: 7, daysAhead: 0 })
          : { competition, daysBack: 7, daysAhead: 0 };
      const { data } = await API.post("/admin/fixtures/sync-results", body);
      const message = data?.message || "Results synced.";
      setAlertModal({ isOpen: true, title: "Success", message: `✅ ${message}`, type: "success" });
      addSyncHistory("Results", "success", message);
      fetchAdminFixtures();
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Failed to sync results.";
      console.error("❌ Error syncing results:", err.response || err);
      setAlertModal({ isOpen: true, title: "Error", message: `❌ ${message}`, type: "error" });
      addSyncHistory("Results", "failed", message);
    }
  };

  const handleSyncFixtures = async () => {
    setAlertModal({ isOpen: true, title: "Syncing", message: "Syncing upcoming fixtures...", type: "info" });
    try {
      const body =
        competition === "world_cup"
          ? buildWorldCupSyncBody({ daysBack: 0, daysAhead: 7 })
          : { competition, daysAhead: 7 };
      const { data } = await API.post("/admin/fixtures/sync-fixtures", body);
      const message = data?.message || "Fixtures synced.";
      setAlertModal({ isOpen: true, title: "Success", message: `✅ ${message}`, type: "success" });
      addSyncHistory("Fixtures", "success", message);
      fetchAdminFixtures();
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Failed to sync fixtures.";
      console.error("❌ Error syncing fixtures:", err.response || err);
      setAlertModal({ isOpen: true, title: "Error", message: `❌ ${message}`, type: "error" });
      addSyncHistory("Fixtures", "failed", message);
    }
  };

  const handleSyncAll = async () => {
    setSyncProgress({
      running: true,
      steps: { standings: "queued", results: "queued", fixtures: "queued" },
      summary: null,
      error: "",
    });

    const runStep = async (key, fn) => {
      setSyncProgress((prev) => ({ ...prev, steps: { ...prev.steps, [key]: "running" } }));
      try {
        const data = await fn();
        setSyncProgress((prev) => ({ ...prev, steps: { ...prev.steps, [key]: "done" } }));
        return { ok: true, data };
      } catch (err) {
        setSyncProgress((prev) => ({ ...prev, steps: { ...prev.steps, [key]: "failed" } }));
        return { ok: false, err };
      }
    };

    try {
      const standingsRes = await runStep("standings", async () => (await API.post("/admin/sync-standings", { competition })).data);
      if (!standingsRes.ok) throw standingsRes.err;

      const resultsRes = await runStep("results", async () => (await API.post("/admin/fixtures/sync-results", { competition, daysBack: 7 })).data);
      if (!resultsRes.ok) throw resultsRes.err;

      const fixturesRes = await runStep("fixtures", async () => (await API.post("/admin/fixtures/sync-fixtures", { competition, daysAhead: 7 })).data);
      if (!fixturesRes.ok) throw fixturesRes.err;

      const summary = {
        standings: standingsRes.data?.message || "Standings synced",
        results: resultsRes.data?.message || "Results synced",
        fixtures: fixturesRes.data?.message || "Fixtures synced",
      };

      setSyncProgress((prev) => ({ ...prev, running: false, summary }));
      setAlertModal({ isOpen: true, title: "Success", message: "✅ Full sync complete.", type: "success" });
      addSyncHistory("Full Sync", "success", "Standings + Results + Fixtures completed.");
      fetchAdminFixtures();
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Failed during full sync.";
      console.error("❌ Error syncing all:", err.response || err);
      setSyncProgress((prev) => ({ ...prev, running: false, error: message }));
      setAlertModal({ isOpen: true, title: "Error", message: `❌ ${message}`, type: "error" });
      addSyncHistory("Full Sync", "failed", message);
    }
  };

  const handleSavePredictionSettings = async () => {
    const value = Number(predictionLockMinutes);
    if (!Number.isFinite(value) || value < 0 || value > 1440) {
      setAlertModal({
        isOpen: true,
        title: "Invalid value",
        message: "❌ Lock time must be between 0 and 1440 minutes.",
        type: "warning",
      });
      return;
    }

    setSavingPredictionSettings(true);
    try {
      const { data } = await API.put("/admin/prediction-settings", {
        competition,
        prediction_lock_minutes: Math.floor(value),
      });
      setAlertModal({
        isOpen: true,
        title: "Updated",
        message: `✅ ${data?.message || "Prediction lock updated."}`,
        type: "success",
      });
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Failed to update setting.";
      setAlertModal({
        isOpen: true,
        title: "Error",
        message: `❌ ${message}`,
        type: "error",
      });
    } finally {
      setSavingPredictionSettings(false);
    }
  };

  const handleSendTestEmail = async () => {
    const toEmail = testEmailTo.trim();
    if (!toEmail) {
      setAlertModal({
        isOpen: true,
        title: "Missing email",
        message: "Enter the recipient email address.",
        type: "error",
      });
      return;
    }

    setSendingTestEmail(true);
    try {
      const { data } = await API.post("/admin/send-test-email", {
        template: testEmailTemplate,
        toEmail,
      });
      setAlertModal({
        isOpen: true,
        title: "Test email sent",
        message: data?.message || "Check your inbox for the test email.",
        type: "success",
      });
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Failed to send test email.";
      setAlertModal({
        isOpen: true,
        title: "Error",
        message: `❌ ${message}`,
        type: "error",
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleRecomputeDeadlines = async () => {
    setRecomputingDeadlines(true);
    try {
      const { data } = await API.post("/admin/prediction-settings/recompute-deadlines", {
        competition,
      });
      setAlertModal({
        isOpen: true,
        title: "Done",
        message:
          `✅ ${data?.message || "Deadlines recomputed."} Updated ${data?.updated ?? 0} fixtures.`,
        type: "success",
      });
      fetchAdminFixtures();
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Failed to recompute deadlines.";
      setAlertModal({
        isOpen: true,
        title: "Error",
        message: `❌ ${message}`,
        type: "error",
      });
    } finally {
      setRecomputingDeadlines(false);
    }
  };

  const tabs = [
    { key: "controls", label: "Controls" },
    { key: "audit", label: "Audit" },
    { key: "users", label: "Users" },
    { key: "matches", label: "Matches & Competitions" },
  ];

  const fetchAdminUsers = async () => {
    setLoadingAdminUsers(true);
    try {
      const { data } = await API.get("/users");
      setAdminUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ Error fetching users:", err);
      setAlertModal({
        isOpen: true,
        title: "Error",
        message: err.response?.data?.message || "Failed to load users.",
        type: "error",
      });
      setAdminUsers([]);
    } finally {
      setLoadingAdminUsers(false);
    }
  };

  useEffect(() => {
    if (activeTab === "users") {
      fetchAdminUsers();
    }
  }, [activeTab]);

  const patchUserFlags = async (userId, body) => {
    setSavingUserId(userId);
    try {
      const { data } = await API.patch(`/users/${userId}`, body);
      setAdminUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, ...data } : u))
      );
    } catch (err) {
      console.error("❌ Error updating user:", err);
      setAlertModal({
        isOpen: true,
        title: "Error",
        message: err.response?.data?.message || "Failed to update user.",
        type: "error",
      });
      fetchAdminUsers();
    } finally {
      setSavingUserId(null);
    }
  };

  const handleCreateGameweek = async () => {
    const {
      week_number,
      start_date,
      end_date,
      prediction_open_time,
      prediction_deadline,
    } = newGameweek;
    if (
      !week_number ||
      !start_date ||
      !end_date ||
      !prediction_open_time ||
      !prediction_deadline
    ) {
      setAlertModal({ isOpen: true, title: "Missing details", message: "❌ Please fill in all round details including open time.", type: "warning" });
      return;
    }
    try {
      const { data } = await API.post("/gameweeks/create-gameweek", {
        ...newGameweek,
        competition,
      });
      setGameweeks([...gameweeks, data]);
      setNewGameweek({
        week_number: "",
        start_date: null,
        end_date: null,
        prediction_open_time: null,
        prediction_deadline: null,
      });
      setAlertModal({ isOpen: true, title: "Success", message: "✅ Round created successfully!", type: "success" });
    } catch (err) {
      console.error("❌ Error creating round:", err.response || err);
      setAlertModal({ isOpen: true, title: "Error", message: "❌ " + (err.response?.data?.message || err.message), type: "error" });
    }
  };

  const updateGameweekDeadline = async () => {
    if (!selectedGameweek || (!newGameweek.prediction_open_time && !newGameweek.prediction_deadline)) {
      setAlertModal({ isOpen: true, title: "Missing selection", message: "❌ Select a round and set a deadline.", type: "warning" });
      return;
    }
    try {
      await API.put(`/gameweeks/update-deadline/${selectedGameweek}`, {
        prediction_open_time: newGameweek.prediction_open_time,
        prediction_deadline: newGameweek.prediction_deadline,
        timezone,
      });
      setAlertModal({ isOpen: true, title: "Success", message: "✅ Open time & deadline updated!", type: "success" });
    } catch (err) {
      console.error("❌ Error updating open/deadline:", err.response || err);
      setAlertModal({ isOpen: true, title: "Error", message: "❌ " + (err.response?.data?.message || err.message), type: "error" });
    }
  };

  const updateGameweekDates = async () => {
    if (!selectedGameweek || !newGameweek.start_date || !newGameweek.end_date) {
      setAlertModal({ isOpen: true, title: "Missing dates", message: "❌ Select a round and both start and end dates.", type: "warning" });
      return;
    }
    try {
      await API.put(`/gameweeks/update-dates/${selectedGameweek}`, {
        start_date: moment(newGameweek.start_date).format("YYYY-MM-DD HH:mm:ss"),
        end_date: moment(newGameweek.end_date).format("YYYY-MM-DD HH:mm:ss"),
      });
      setAlertModal({ isOpen: true, title: "Success", message: "✅ Dates updated!", type: "success" });
    } catch (err) {
      console.error("❌ Error updating dates:", err.response || err);
      setAlertModal({ isOpen: true, title: "Error", message: "❌ Failed to update dates.", type: "error" });
    }
  };

  // Bulk match helpers
  const addBulkMatchRow = () => {
    setBulkMatches((rows) => [
      ...rows,
      { home_team: "", away_team: "", match_date: null },
    ]);
  };

  const updateBulkMatchRow = (index, field, value) => {
    setBulkMatches((rows) =>
      rows.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      )
    );
  };

  const clearBulkMatches = () => {
    setBulkMatches([{ home_team: "", away_team: "", match_date: null }]);
  };

  const handleBulkAddMatches = async () => {
    // Build payloads from filled rows only
    const payloads = bulkMatches
      .filter(
        (row) =>
          row.home_team &&
          row.away_team &&
          row.match_date
      )
      .map((row) => ({
        gameweekId: null,
        homeTeam: row.home_team,
        awayTeam: row.away_team,
        matchDate: row.match_date.toISOString(),
        competition,
        stage: null,
      }));

    if (payloads.length === 0) {
      setAlertModal({
        isOpen: true,
        title: "Nothing to add",
        message: "⚠️ Fill at least one complete row (home, away, date).",
        type: "warning",
      });
      return;
    }

    try {
      const created = [];
      const errors = [];

      for (const payload of payloads) {
        try {
          const { data } = await API.post(
            "/gameweeks/add-fixture",
            payload
          );
          created.push(data);
        } catch (err) {
          console.error("❌ Error adding bulk match:", err.response || err);
          errors.push(
            err.response?.data?.message || err.message || "Unknown error"
          );
        }
      }

      if (created.length) {
        setFixtures((prev) => [...prev, ...created]);
      }
      clearBulkMatches();

      setAlertModal({
        isOpen: true,
        title: "Bulk add complete",
        message:
          created.length && !errors.length
            ? `✅ Added ${created.length} fixtures.`
            : `Added ${created.length} fixtures, ${errors.length} failed. Check console for details.`,
        type: errors.length ? "warning" : "success",
      });
    } catch (err) {
      console.error("❌ Bulk add error:", err.response || err);
      setAlertModal({
        isOpen: true,
        title: "Error",
        message:
          "❌ " +
          (err.response?.data?.message || err.message || "Bulk add failed."),
        type: "error",
      });
    }
  };

  const handleAddMatch = async () => {
    const { home_team, away_team, match_date } = newMatch;
    if (
      !home_team ||
      !away_team ||
      !match_date
    ) {
      setAlertModal({ isOpen: true, title: "Missing details", message: "❌ Fill in all match details.", type: "warning" });
      return;
    }
    try {
      const { data } = await API.post("/gameweeks/add-fixture", {
        gameweekId: null,
        homeTeam: home_team,
        awayTeam: away_team,
        matchDate: match_date.toISOString(),
        competition,
        stage: null,
      });
      setFixtures([...fixtures, data]);
      setNewMatch({ home_team: "", away_team: "", match_date: null });
      setAlertModal({ isOpen: true, title: "Success", message: "✅ Match added!", type: "success" });
    } catch (err) {
      console.error("❌ Error adding match:", err.response || err);
      setAlertModal({ isOpen: true, title: "Error", message: "❌ " + (err.response?.data?.message || err.message), type: "error" });
    }
  };

  const handleUpdateScore = async (fixtureId, idx) => {
    const m = fixtures[idx];
    if (isNaN(m.home_score) || isNaN(m.away_score)) {
      setAlertModal({ isOpen: true, title: "Invalid scores", message: "❌ Enter valid scores.", type: "warning" });
      return;
    }
    try {
      await API.put(`/gameweeks/update-result/${fixtureId}`, {
        home_score: m.home_score,
        away_score: m.away_score,
      });
      setAlertModal({ isOpen: true, title: "Success", message: "✅ Score updated!", type: "success" });
    } catch (err) {
      console.error("❌ Error updating score:", err.response || err);
      setAlertModal({ isOpen: true, title: "Error", message: "❌ " + (err.response?.data?.message || err.message), type: "error" });
    }
  };

  const togglePostponeFixture = async (fixtureId, isCurrentlyPostponed, deadline = null) => {
    if (!isCurrentlyPostponed && !deadline) {
      setAlertModal({ isOpen: true, title: "Missing deadline", message: "❌ Select a postponement deadline.", type: "warning" });
      return;
    }
    try {
      await API.put(`/gameweeks/toggle-postpone/${fixtureId}`, {
        postponement_deadline: deadline,
      });
      setFixtures((f) =>
        f.map((fx) =>
          fx.id === fixtureId
            ? { ...fx, postponed: !isCurrentlyPostponed, postponement_deadline: deadline }
            : fx
        )
      );
      setAlertModal({ isOpen: true, title: "Success", message: `✅ Fixture ${isCurrentlyPostponed ? "unpostponed" : "postponed"}!`, type: "success" });
      setPostponePickerFixture(null);
      setPostponePickerDate(null);
    } catch (err) {
      console.error("❌ Error toggling postpone:", err.response || err);
      setAlertModal({ isOpen: true, title: "Error", message: "❌ " + (err.response?.data?.message || err.message), type: "error" });
    }
  };

  const handleRemoveFixture = async (fixtureId) => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Fixture",
      message: "⚠️ Delete this fixture?",
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: "", message: "", onConfirm: null });
        try {
          await API.delete(`/gameweeks/delete-fixture/${fixtureId}`);
          setFixtures((f) => f.filter((fx) => fx.id !== fixtureId));
          setAlertModal({ isOpen: true, title: "Success", message: "✅ Fixture deleted!", type: "success" });
        } catch (err) {
          console.error("❌ Error deleting fixture:", err.response || err);
          setAlertModal({ isOpen: true, title: "Error", message: "❌ " + (err.response?.data?.message || err.message), type: "error" });
        }
      }
    });
  };

  const toggleTableLock = async (lock) => {
    try {
      const { data } = await API.put("/table-predictions/toggle-lock", { lock, competition });
      setTableLockStatus({
        locked: !!data?.locked,
        manualLock: !!data?.manualLock,
        pastDeadline: !!data?.pastDeadline,
        deadline: data?.deadline || tableLockStatus.deadline,
      });
      setAlertModal({
        isOpen: true,
        title: "Success",
        message: `✅ Table Predictions ${lock ? "Locked" : "Unlocked"}!`,
        type: "success",
      });
    } catch (err) {
      console.error("❌ Error toggling lock:", err.response || err);
      setAlertModal({ isOpen: true, title: "Error", message: "❌ " + (err.response?.data?.message || err.message), type: "error" });
    }
  };

  const handleClearTableDeadline = async () => {
    setSavingTableDeadline(true);
    try {
      const { data } = await API.put("/admin/table-prediction-settings", {
        competition,
        table_prediction_deadline: null,
      });
      setTableDeadline(null);
      setTableLockStatus({
        locked: !!data?.locked,
        manualLock: !!data?.manualLock,
        pastDeadline: !!data?.pastDeadline,
        deadline: null,
      });
      setAlertModal({
        isOpen: true,
        title: "Updated",
        message: data?.message || "Table prediction deadline cleared.",
        type: "success",
      });
    } catch (err) {
      setAlertModal({
        isOpen: true,
        title: "Error",
        message: err.response?.data?.message || err.message || "Failed to clear deadline.",
        type: "error",
      });
    } finally {
      setSavingTableDeadline(false);
    }
  };

  const handleSaveTableDeadline = async () => {
    setSavingTableDeadline(true);
    try {
      const { data } = await API.put("/admin/table-prediction-settings", {
        competition,
        table_prediction_deadline: tableDeadline ? tableDeadline.toISOString() : null,
      });
      setTableLockStatus({
        locked: !!data?.locked,
        manualLock: !!data?.manualLock,
        pastDeadline: !!data?.pastDeadline,
        deadline: data?.deadline || null,
      });
      setAlertModal({
        isOpen: true,
        title: "Updated",
        message: data?.message || "Table prediction deadline updated.",
        type: "success",
      });
    } catch (err) {
      setAlertModal({
        isOpen: true,
        title: "Error",
        message: err.response?.data?.message || err.message || "Failed to save deadline.",
        type: "error",
      });
    } finally {
      setSavingTableDeadline(false);
    }
  };

  const handleBatchSave = async () => {
    // Collect all fixtures with scores
    const results = fixtures
      .filter(f => f.home_score !== null && f.home_score !== undefined && 
                  f.away_score !== null && f.away_score !== undefined)
      .map(f => ({
        fixtureId: f.id,
        home_score: parseInt(f.home_score),
        away_score: parseInt(f.away_score),
        manual_override: !!f.manual_override,
      }));

    if (results.length === 0) {
      setAlertModal({ isOpen: true, title: "Nothing to save", message: "⚠️ No scores to save. Please enter some scores first.", type: "warning" });
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: "Save All Scores",
      message: `💾 Save ${results.length} fixture scores?`,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: "", message: "", onConfirm: null });
        try {
          const response = await API.put("/gameweeks/batch-update-results", { results });

          if (response.data.errors && response.data.errors.length > 0) {
            setAlertModal({ isOpen: true, title: "Partial errors", message: `⚠️ ${response.data.errors.length} errors occurred. Check console for details.`, type: "warning" });
            console.error("Batch save errors:", response.data.errors);
          } else {
            setAlertModal({ isOpen: true, title: "Success", message: `✅ ${response.data.message}` , type: "success" });
          }

          await fetchAdminFixtures();
        } catch (err) {
          console.error("❌ Error in batch save:", err.response || err);
          setAlertModal({ isOpen: true, title: "Error", message: "❌ " + (err.response?.data?.message || err.message), type: "error" });
        }
      }
    });
  };

  return (
    <div className="min-h-screen bg-paper text-ink">
      <main className="max-w-7xl mx-auto px-4 md:px-6 pt-5 md:pt-8 pb-8 space-y-6">
        <div>
          <h1 className="text-[22px] md:text-[28px] font-bold text-ink">Admin</h1>
          <p className="mt-1 text-meta text-muted">
            Fixtures, results, prediction windows, and who can play.
          </p>
        </div>
        {/* Top Control Panel */}
        <Card className="sticky top-4 z-10">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex-1 min-w-0 overflow-x-auto pb-1 -mx-1 px-1">
                <div className="flex flex-nowrap gap-2 w-max">
                  {tabs.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setActiveTab(t.key)}
                      className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                        activeTab === t.key
                          ? "bg-brand text-white"
                          : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="text-xs text-gray-500 font-medium">
                {tabMeta[activeTab]?.hint}
              </div>
            </div>

            {activeTab !== "users" && (
            <div className="overflow-x-auto pb-1">
              <div className="inline-flex min-w-full md:min-w-0 gap-2">
                {competitions.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => handleCompetitionChange(c.key)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                      competition === c.key
                        ? "bg-brand text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            )}

            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {activeTab === "users" ? (
                <span className="font-semibold text-gray-800">Users &amp; permissions</span>
              ) : (
                <>
                  Active: <span className="font-semibold">{competitions.find((c) => c.key === competition)?.label || competition}</span> /{" "}
                  <span className="font-semibold">{tabMeta[activeTab]?.title}</span>
                </>
              )}
            </div>
          </div>
        </Card>

      {/* Controls Tab */}
      {activeTab === "controls" && (
        <>
          <Card title="⏱ Prediction Lock Window">
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Lock predictions this many minutes before kickoff for{" "}
                <span className="font-semibold">
                  {competitions.find((c) => c.key === competition)?.label || competition}
                </span>.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="number"
                  min={0}
                  max={1440}
                  value={predictionLockMinutes}
                  onChange={(e) => setPredictionLockMinutes(e.target.value)}
                  className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                />
                <span className="text-sm text-gray-500">minutes before kickoff</span>
                <button
                  onClick={handleSavePredictionSettings}
                  disabled={savingPredictionSettings}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    savingPredictionSettings
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-brand text-white hover:bg-brand-hover"
                  }`}
                >
                  {savingPredictionSettings ? "Saving..." : "Save Setting"}
                </button>
                <button
                  onClick={handleRecomputeDeadlines}
                  disabled={recomputingDeadlines}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    recomputingDeadlines
                      ? "bg-sky-300 text-white cursor-not-allowed"
                      : "bg-sky-600 text-white hover:bg-sky-700"
                  }`}
                >
                  {recomputingDeadlines ? "Updating..." : "Apply to Existing Upcoming Fixtures"}
                </button>
              </div>
            </div>
          </Card>

          <Card title="🔄 Sync Operations">
            <div className="space-y-4">
              {competition === "world_cup" && (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 space-y-3">
                  <p className="text-sm text-indigo-900 font-medium">
                    World Cup 2026 — use Sync Fixtures / Results (next 7 days) for UAT.
                    Optional full sync pulls every match for the selected stage.
                  </p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <label className="text-sm text-indigo-900 font-medium shrink-0">
                      Sync stage
                    </label>
                    <select
                      value={wcSyncStage}
                      onChange={(e) => setWcSyncStage(e.target.value)}
                      className="p-2 border border-indigo-200 rounded-lg bg-white text-sm min-w-[200px]"
                    >
                      {WC_STAGE_OPTIONS.map((opt) => (
                        <option key={opt.id || "all"} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={handleSyncWorldCupFull}
                    className="px-4 py-3 bg-indigo-700 text-white rounded-lg hover:bg-indigo-800 transition-all duration-200 font-semibold text-sm"
                  >
                    🌍 Sync Full Schedule (selected stage)
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                <button
                  onClick={handleSyncStandings}
                  disabled={!["premier_league", "laliga", "bundesliga", "serie_a"].includes(competition)}
                  className={`px-4 py-3 rounded-lg transition-all duration-200 font-semibold text-sm ${
                    ["premier_league", "laliga", "bundesliga", "serie_a"].includes(competition)
                      ? "bg-indigo-600 text-white hover:bg-indigo-700"
                      : "bg-gray-200 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  🔄 Sync Standings
                </button>
                <button
                  onClick={handleSyncResults}
                  className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 font-semibold text-sm"
                >
                  🧾 Sync Results (Last 7 Days)
                </button>
                <button
                  onClick={handleSyncFixtures}
                  className="px-4 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-all duration-200 font-semibold text-sm"
                >
                  📅 Sync Fixtures (Next 7 Days)
                </button>
                <button
                  onClick={handleSyncAll}
                  disabled={
                    syncProgress.running ||
                    !["premier_league", "laliga", "bundesliga", "serie_a"].includes(competition)
                  }
                  className={`px-4 py-3 rounded-lg transition-all duration-200 font-semibold text-sm ${
                    syncProgress.running ||
                    !["premier_league", "laliga", "bundesliga", "serie_a"].includes(competition)
                      ? "bg-emerald-300 text-white cursor-not-allowed"
                      : "bg-emerald-600 text-white hover:bg-emerald-700"
                  }`}
                >
                  {syncProgress.running ? "⏳ Running Full Sync..." : "✅ Run Full Sync"}
                </button>
              </div>

              {(syncProgress.running || syncProgress.summary || syncProgress.error) && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                  <div className="text-sm font-semibold text-gray-800">Full sync progress</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                    {[
                      { key: "standings", label: "Standings" },
                      { key: "results", label: "Results" },
                      { key: "fixtures", label: "Fixtures" },
                    ].map((step) => (
                      <div key={step.key} className="flex items-center justify-between rounded border bg-white px-2 py-1.5">
                        <span className="font-medium text-gray-700">{step.label}</span>
                        <span
                          className={`font-semibold capitalize border rounded-full px-2 py-0.5 ${getSyncStatusClass(
                            syncProgress.steps[step.key]
                          )}`}
                        >
                          {syncProgress.steps[step.key]}
                        </span>
                      </div>
                    ))}
                  </div>
                  {syncProgress.summary && (
                    <div className="text-xs text-gray-700 space-y-1">
                      <div>• {syncProgress.summary.standings}</div>
                      <div>• {syncProgress.summary.results}</div>
                      <div>• {syncProgress.summary.fixtures}</div>
                    </div>
                  )}
                  {syncProgress.error && <div className="text-xs text-red-600">{syncProgress.error}</div>}
                </div>
              )}

              {syncHistory.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-white">
                  <div className="px-3 py-2 border-b text-sm font-semibold text-gray-800">Last Sync Runs</div>
                  <div className="divide-y">
                    {syncHistory.map((item, idx) => (
                      <div key={`${item.at}-${idx}`} className="px-3 py-2 text-xs flex items-center justify-between gap-3">
                        <div className="text-gray-700">
                          <span className="font-semibold">{item.action}</span> - {item.message}
                        </div>
                        <div className={`font-semibold ${item.status === "success" ? "text-green-600" : "text-red-600"}`}>
                          {item.status}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card title="🔒 Table Predictions Lock">
              {["premier_league", "laliga", "bundesliga", "serie_a"].includes(competition) ? (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>
                      Status:{" "}
                      <span className={`font-semibold ${tableLockStatus.locked ? "text-red-700" : "text-green-700"}`}>
                        {tableLockStatus.locked ? "Closed" : "Open"}
                      </span>
                      {tableLockStatus.manualLock && " (manual lock)"}
                      {tableLockStatus.pastDeadline && " (deadline passed)"}
                    </div>
                    {tableLockStatus.deadline && (
                      <div>
                        Deadline:{" "}
                        <span className="font-medium">
                          {new Date(tableLockStatus.deadline).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <DatePicker
                      selected={tableDeadline}
                      onChange={(date) => setTableDeadline(date)}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="yyyy-MM-dd HH:mm"
                      placeholderText="Set close date/time (optional)"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[220px]"
                    />
                    <button
                      type="button"
                      onClick={handleSaveTableDeadline}
                      disabled={savingTableDeadline}
                      className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover font-semibold text-sm disabled:opacity-60"
                    >
                      {savingTableDeadline ? "Saving..." : "Save deadline"}
                    </button>
                    <button
                      type="button"
                      onClick={handleClearTableDeadline}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                    >
                      Clear deadline
                    </button>
                  </div>

                  <div className="flex gap-4 pt-1">
                    <button
                      onClick={() => toggleTableLock(true)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 font-semibold"
                    >
                      🔒 Lock now
                    </button>
                    <button
                      onClick={() => toggleTableLock(false)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 font-semibold"
                    >
                      🔓 Unlock
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Manual lock closes immediately. Deadline auto-closes at the set time (UTC in picker).
                    Either blocks new submissions.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  Table prediction deadlines apply to Premier League, LaLiga, Bundesliga, and Serie A.
                </p>
              )}
            </Card>

          <Card title="✉️ Test email templates">
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Send a sample email to any address. Subject is prefixed with{" "}
                <span className="font-mono text-gray-800">[Test]</span>. Uses the same layout as
                live notifications (white card on gray background).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Send to
                  </label>
                  <input
                    type="email"
                    value={testEmailTo}
                    onChange={(e) => setTestEmailTo(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Template
                  </label>
                  <select
                    value={testEmailTemplate}
                    onChange={(e) => setTestEmailTemplate(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors bg-white"
                  >
                    <option value="verify">Verify email</option>
                    <option value="reset">Reset password</option>
                    <option value="open">Predictions open</option>
                    <option value="24h">24h reminder</option>
                    <option value="1h">1h reminder</option>
                    <option value="weekly">Weekly digest</option>
                  </select>
                </div>
              </div>
              <button
                type="button"
                onClick={handleSendTestEmail}
                disabled={sendingTestEmail}
                className="px-5 py-3 bg-brand text-white rounded-lg font-semibold hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sendingTestEmail ? "Sending…" : "Send test email"}
              </button>
            </div>
          </Card>
        </>
      )}

      {/* Matches & Competitions Tab (GW-free) */}
      {activeTab === "matches" && (
        <>
          {["premier_league", "laliga", "bundesliga", "serie_a"].includes(competition) && (
            <AdminLeagueTable competition={competition} />
          )}
        </>
      )}

      {activeTab === "matches" && (
        <>
          {/* Add Match Section (single quick match) */}
          <Card title="➕ Add Single Match (quick fix)">
            <div className="space-y-3">
              {/* Shared team list for typeahead dropdowns */}
              <datalist id="team-options">
                {teamOptions.map((t) => (
                  <option key={`opt-${t.id || t.team_name}`} value={t.team_name} />
                ))}
              </datalist>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input
                  list="team-options"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                  placeholder={loadingTeams ? "Loading teams..." : "Home team"}
                  value={newMatch.home_team}
                  onChange={(e) => setNewMatch({ ...newMatch, home_team: e.target.value })}
                  disabled={loadingTeams}
                />
                <input
                  list="team-options"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                  placeholder={loadingTeams ? "Loading teams..." : "Away team"}
                  value={newMatch.away_team}
                  onChange={(e) => setNewMatch({ ...newMatch, away_team: e.target.value })}
                  disabled={loadingTeams}
                />

                <DatePicker
                  selected={newMatch.match_date}
                  onChange={(d) => setNewMatch({ ...newMatch, match_date: d })}
                  showTimeSelect
                  timeIntervals={15}
                  dateFormat="Pp"
                  placeholderText="Match Date"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleAddMatch}
                  className="px-5 py-2.5 bg-brand text-white rounded-lg hover:bg-brand-hover transition-all duration-200 font-semibold"
                >
                  ➕ Add Match
                </button>
              </div>
            </div>
          </Card>

          {/* Bulk Add Matches */}
          <Card title="🧾 Bulk Add Matches">
            <p className="text-sm text-gray-600 mb-3">
              Fill as many rows as you need (e.g. all 10 weekend fixtures), then click
              &quot;Add All Matches&quot;. No gameweek or stage needed.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-2 text-left font-semibold text-gray-700">Home</th>
                    <th className="p-2 text-left font-semibold text-gray-700">Away</th>
                    <th className="p-2 text-left font-semibold text-gray-700">Date &amp; Time</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkMatches.map((row, idx) => (
                    <tr key={idx} className="border-b border-gray-200">
                      <td className="p-2">
                        <input
                          list="team-options"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                          placeholder={loadingTeams ? "Loading…" : "Home team"}
                          value={row.home_team}
                          onChange={(e) =>
                            updateBulkMatchRow(idx, "home_team", e.target.value)
                          }
                          disabled={loadingTeams}
                        />
                      </td>
                      <td className="p-2">
                        <input
                          list="team-options"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                          placeholder={loadingTeams ? "Loading…" : "Away team"}
                          value={row.away_team}
                          onChange={(e) =>
                            updateBulkMatchRow(idx, "away_team", e.target.value)
                          }
                          disabled={loadingTeams}
                        />
                      </td>
                      <td className="p-2">
                        <DatePicker
                          selected={row.match_date}
                          onChange={(d) =>
                            updateBulkMatchRow(idx, "match_date", d)
                          }
                          showTimeSelect
                          timeIntervals={15}
                          dateFormat="Pp"
                          placeholderText="Match Date"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={addBulkMatchRow}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 transition-all duration-200 font-semibold"
              >
                ➕ Add Row
              </button>
              <button
                type="button"
                onClick={clearBulkMatches}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 transition-all duration-200 font-semibold"
              >
                🧹 Clear
              </button>
              <button
                type="button"
                onClick={handleBulkAddMatches}
                className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover transition-all duration-200 font-semibold"
              >
                💾 Add All Matches
              </button>
            </div>
          </Card>

          {/* Fixtures Management Table */}
          <Card 
            title="⚽ Manage Fixtures"
            action={
              <button
                onClick={handleBatchSave}
                className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover transition-all duration-200 font-semibold"
              >
                💾 Save All Scores
              </button>
            }
          >
              {loadingFixtures ? (
                <LoadingSpinner message="Loading fixtures..." />
              ) : fixtures.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No fixtures available.</p>
              ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-3 text-left font-semibold text-gray-700">Home</th>
                          <th className="p-3 text-left font-semibold text-gray-700">Score</th>
                        <th className="p-3 text-left font-semibold text-gray-700">Away</th>
                        <th className="p-3 text-left font-semibold text-gray-700">Score</th>
                        <th className="p-3 text-left font-semibold text-gray-700">Date</th>
                        <th className="p-3 text-left font-semibold text-gray-700">Stage</th>
                          <th className="p-3 text-left font-semibold text-gray-700">Status</th>
                          <th className="p-3 text-left font-semibold text-gray-700">Override</th>
                          <th className="p-3 text-left font-semibold text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                      {/* Filters */}
                      <tr>
                        <td colSpan={9} className="p-3">
                          <div className="flex flex-wrap gap-2 items-center text-sm">
                            <span className="font-semibold text-gray-700">Filter:</span>
                            {[
                              { key: "last7", label: "Last 7 days" },
                              { key: "next7", label: "Next 7 days" },
                              { key: "custom", label: "Custom range" },
                            ].map((b) => (
                              <button
                                key={b.key}
                                onClick={() => {
                                  setFilterPreset(b.key);
                                  if (b.key !== "custom") {
                                    setFilterStart(null);
                                    setFilterEnd(null);
                                  }
                                }}
                                className={`px-3 py-1 rounded-full border text-sm ${
                                  filterPreset === b.key
                                    ? "bg-brand text-white"
                                    : "bg-white text-gray-800 border-gray-300"
                                }`}
                              >
                                {b.label}
                              </button>
                            ))}
                            {filterPreset === "custom" && (
                              <>
                                <DatePicker
                                  selected={filterStart}
                                  onChange={setFilterStart}
                                  placeholderText="Start"
                                  className="p-2 border border-gray-300 rounded-lg"
                                />
                                <DatePicker
                                  selected={filterEnd}
                                  onChange={setFilterEnd}
                                  placeholderText="End"
                                  className="p-2 border border-gray-300 rounded-lg"
                                />
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {filteredFixtures.map((m, idx) => (
                        <tr key={m.id} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="p-3 font-medium">{m.home_team}</td>
                          <td className="p-3">
                            <input
                              type="number"
                              className="w-16 p-2 border border-gray-300 rounded-lg text-center focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                              value={m.home_score ?? ""}
                              onChange={(e) => {
                                const copy = [...fixtures];
                                copy[idx].home_score = e.target.value;
                                setFixtures(copy);
                              }}
                            />
                          </td>
                          <td className="p-3 font-medium">{m.away_team}</td>
                          <td className="p-3">
                            <input
                              type="number"
                              className="w-16 p-2 border border-gray-300 rounded-lg text-center focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                              value={m.away_score ?? ""}
                              onChange={(e) => {
                                const copy = [...fixtures];
                                copy[idx].away_score = e.target.value;
                                setFixtures(copy);
                              }}
                            />
                          </td>
                          <td className="p-3 text-sm text-gray-600">{new Date(m.match_date).toLocaleString()}</td>
                      <td className="p-2 text-center">
                        {competition === "ucl" ? m.stage || "-" : "-"}
                      </td>
                      <td className="p-2">
                        {(() => {
                          const st = (m.status || "").toLowerCase();
                          if (st === "finished") {
                            return <span className="bg-green-600 text-white px-2 py-1 rounded text-sm font-semibold">Finished</span>;
                          }
                          if (st === "postponed") {
                            return (
                              <>
                                <span className="bg-yellow-500 text-white px-2 py-1 rounded text-sm font-semibold">Postponed</span>
                                {m.postponement_deadline && (
                                  <div className="text-sm text-gray-700 mt-1">
                                    Deadline: {new Date(m.postponement_deadline).toLocaleString()}
                                  </div>
                                )}
                              </>
                            );
                          }
                          if (st === "live") {
                            return <span className="bg-red-500 text-white px-2 py-1 rounded text-sm font-semibold">Live</span>;
                          }
                          return <span className="bg-gray-500 text-white px-2 py-1 rounded text-sm font-semibold">Scheduled</span>;
                        })()}
                        {m.manual_override && (
                          <div className="mt-1 text-xs font-semibold text-amber-700 bg-amber-100 inline-block px-2 py-0.5 rounded">
                            Override locked
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        <label className="inline-flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={!!m.manual_override}
                            onChange={(e) => {
                              const copy = [...fixtures];
                              copy[idx].manual_override = e.target.checked;
                              setFixtures(copy);
                            }}
                          />
                          <span>Lock</span>
                        </label>
                      </td>
                      <td className="p-2">
                        <div className="flex flex-col gap-2">
                          {m.postponed ? (
                            <button
                              onClick={() => togglePostponeFixture(m.id, true, null)}
                              className="px-3 py-1.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-all duration-200 font-semibold text-sm"
                            >
                              Unpostpone
                            </button>
                          ) : postponePickerFixture === m.id ? (
                            <>
                              <DatePicker
                                selected={postponePickerDate}
                                onChange={setPostponePickerDate}
                                showTimeSelect
                                timeIntervals={15}
                                dateFormat="Pp"
                                placeholderText="Select deadline"
                                className="w-full p-2 border border-gray-300 rounded-lg focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors mb-2"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() =>
                                    togglePostponeFixture(
                                      m.id,
                                      false,
                                      moment(postponePickerDate).format("YYYY-MM-DD HH:mm:ss")
                                    )
                                  }
                                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 font-semibold text-sm"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => {
                                    setPostponePickerFixture(null);
                                    setPostponePickerDate(null);
                                  }}
                                  className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 font-semibold text-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                            </>
                          ) : (
                            <button
                              onClick={() => setPostponePickerFixture(m.id)}
                              className="px-3 py-1.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-all duration-200 font-semibold text-sm"
                            >
                              Postpone
                            </button>
                          )}
                          <button
                            onClick={() => handleUpdateScore(m.id, idx)}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 font-semibold text-sm"
                          >
                            💾 Save Score
                          </button>
                          <button
                            onClick={() => handleRemoveFixture(m.id)}
                            className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 font-semibold text-sm"
                          >
                            ❌ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                    </tbody>
                  </table>
                </div>
              )}
          </Card>
        </>
      )}

      {activeTab === "audit" && <AdminAuditPanel />}

      {activeTab === "users" && (
        <Card title="User accounts">
          <p className="text-sm text-gray-600 mb-4">
            Free tier: verified users can use all leagues. Toggle admin, table predictions, or
            verification (e.g. support recovery).
          </p>
          {loadingAdminUsers ? (
            <LoadingSpinner message="Loading users..." />
          ) : adminUsers.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No users found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-2 text-left font-semibold text-gray-700">ID</th>
                    <th className="p-2 text-left font-semibold text-gray-700">Email</th>
                    <th className="p-2 text-left font-semibold text-gray-700">Username</th>
                    <th className="p-2 text-center font-semibold text-gray-700">Verified</th>
                    <th className="p-2 text-center font-semibold text-gray-700">Admin</th>
                    <th className="p-2 text-center font-semibold text-gray-700">Table preds</th>
                    <th className="p-2 text-left font-semibold text-gray-700">Last login</th>
                    <th className="p-2 text-left font-semibold text-gray-700">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {adminUsers.map((u) => (
                    <tr key={u.id} className="border-b border-gray-200">
                      <td className="p-2 font-mono text-gray-600">{u.id}</td>
                      <td className="p-2 max-w-[200px] break-all">{u.email}</td>
                      <td className="p-2">{u.username}</td>
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                          checked={!!u.email_verified}
                          disabled={savingUserId === u.id}
                          onChange={(e) =>
                            patchUserFlags(u.id, { email_verified: e.target.checked })
                          }
                        />
                      </td>
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                          checked={!!u.is_admin}
                          disabled={savingUserId === u.id}
                          onChange={(e) =>
                            patchUserFlags(u.id, { is_admin: e.target.checked })
                          }
                        />
                      </td>
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                          checked={!!u.has_table_prediction_access}
                          disabled={savingUserId === u.id}
                          onChange={(e) =>
                            patchUserFlags(u.id, {
                              has_table_prediction_access: e.target.checked,
                            })
                          }
                        />
                      </td>
                      <td className="p-2 text-gray-600 whitespace-nowrap">
                        {u.last_login_at
                          ? new Date(u.last_login_at).toLocaleString()
                          : "—"}
                      </td>
                      <td className="p-2 text-gray-600 whitespace-nowrap">
                        {u.created_at
                          ? new Date(u.created_at).toLocaleString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4">
            <button
              type="button"
              onClick={fetchAdminUsers}
              disabled={loadingAdminUsers}
              className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 font-semibold text-sm"
            >
              Refresh list
            </button>
          </div>
        </Card>
      )}
      </main>
      
      {/* Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: "", message: "", onConfirm: null })}
      />
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ isOpen: false, title: "", message: "", type: "info" })}
      />
    </div>
  );
};

export default AdminDashboard;
